import type {
  ClashWorkerRequest,
  ClashWorkerResponse,
} from "./cad-clash-worker";

const REQUEST_TIMEOUT_MS = 120_000;

/** One correlated request at a time for a stateful clash worker. */
export class ClashWorkerClient {
  private sequence = 0;
  private busy = false;

  /**
   * @param worker Owned worker, terminated by the job coordinator.
   * @param signal Whole-job cancellation.
   */
  constructor(
    readonly worker: Worker,
    private readonly signal: AbortSignal,
  ) {}

  /**
   * @param request One bounded operation.
   * @returns The matching worker response.
   */
  send(request: ClashWorkerRequest): Promise<ClashWorkerResponse> {
    if (this.signal.aborted) return Promise.reject(this.signal.reason);
    if (this.busy) {
      return Promise.reject(
        new Error("Clash worker already has an active request"),
      );
    }
    this.busy = true;
    const requestId = ++this.sequence;
    const signal = AbortSignal.any([
      this.signal,
      AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    ]);
    return new Promise((resolve, reject) => {
      const cleanup = (): void => {
        this.busy = false;
        this.worker.removeEventListener("message", message);
        this.worker.removeEventListener("error", error);
        this.worker.removeEventListener("messageerror", invalidMessage);
        signal.removeEventListener("abort", abort);
      };
      function message(event: MessageEvent<ClashWorkerResponse>): void {
        if (event.data.requestId !== requestId) return;
        cleanup();
        if (event.data.error) reject(new Error(event.data.error));
        else resolve(event.data);
      }
      function error(event: ErrorEvent): void {
        cleanup();
        reject(new Error(event.message));
      }
      function invalidMessage(): void {
        cleanup();
        reject(new Error("Invalid clash worker response"));
      }
      function abort(): void {
        cleanup();
        reject(signal.reason);
      }
      this.worker.addEventListener("message", message);
      this.worker.addEventListener("error", error);
      this.worker.addEventListener("messageerror", invalidMessage);
      signal.addEventListener("abort", abort, { once: true });
      try {
        this.worker.postMessage(
          { ...request, requestId },
          "positions" in request
            ? [
                request.positions.buffer,
                ...("eligible" in request && request.eligible
                  ? [request.eligible.buffer]
                  : []),
              ]
            : [],
        );
      } catch (error) {
        cleanup();
        reject(error);
      }
    });
  }
}

/**
 * @param budget Existing job memory policy, in bytes.
 * @param geometryBytes Complete raw CAD boundary bytes.
 * @param cores Available logical CPUs; reserve capacity for the viewer.
 * @returns A conservative CPU and memory-aware pool size.
 */
export function clashWorkerCount(
  budget: number,
  geometryBytes: number,
  cores: number,
): 1 | 2 {
  const coordinatorBytes = 64 * 1024 * 1024;
  const cellWorkingBytes = 128 * 1024 * 1024;
  const geometryOverhead = 32;
  return cores >= 4 &&
    budget >=
      coordinatorBytes +
        2 * (cellWorkingBytes + geometryBytes * geometryOverhead)
    ? 2
    : 1;
}
